import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Menu,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import type { RootState } from "../../redux/store";

import {
  getSalesNotifications,
  getSalesNotificationUnreadCount,
  markAllSalesNotificationsRead,
  markSalesNotificationRead,
  type SalesNotification,
} from "../../redux/apis/sales/salesNotificationApis";
import { getChatSocket } from "../../config/chatSocket";

// Top-right bell + badge for SALES users only. The retailer bell and its
// notification system are separate and untouched.
const SalesNotificationMenu: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [items, setItems] = useState<SalesNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  // "All" vs the currently-selected customer's notifications.
  const [tab, setTab] = useState<"all" | "customer">("all");
  const selectedCustomer = useSelector((s: RootState) => s.auth.selectedCustomer);
  const selectedCustomerNumber = selectedCustomer ? Number(selectedCustomer.C_Number) : null;
  const selectedCustomerName =
    (selectedCustomer?.C_Name || (selectedCustomer as { C_CoName?: string } | null)?.C_CoName || "")
      .trim() || "Customer";

  // The rows for the active tab — all of them, or just the selected customer's.
  const shownItems =
    tab === "customer" && selectedCustomerNumber != null
      ? items.filter((n) => Number(n.metadata?.customerNumber) === selectedCustomerNumber)
      : items;

  const refreshCount = useCallback(async () => {
    try {
      setUnread(await getSalesNotificationUnreadCount());
    } catch {
      /* best-effort badge */
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await getSalesNotifications());
      await refreshCount();
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();

    // Delivery is socket-only: the server pushes `sales:notification` to this
    // user's personal room. No FCM, no device token.
    const socket = getChatSocket();
    const onNotification = (evt: { title: string; description: string }) => {
      refreshCount();
      toast(
        (t) => (
          <Box sx={{ maxWidth: 320 }} onClick={() => toast.dismiss(t.id)}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{evt.title}</Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{evt.description}</Typography>
          </Box>
        ),
        { position: "top-right", duration: 6000, icon: "🔔" }
      );
    };
    socket.on("sales:notification", onNotification);
    // Opening/reading a chat clears its bell entry server-side — resync the badge.
    socket.on("chat:read", refreshCount);
    return () => {
      socket.off("sales:notification", onNotification);
      socket.off("chat:read", refreshCount);
    };
  }, [refreshCount]);

  const open = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    loadList();
  };

  const handleClick = async (n: SalesNotification) => {
    if (!n.isRead) {
      try {
        await markSalesNotificationRead(n.id);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
        refreshCount();
      } catch {
        /* ignore */
      }
    }
    setAnchorEl(null);
    // Land on the actual thread, not just the chat list. 'delivery_misconduct' has
    // no conversation behind it (it only pings the rep), so that one still falls
    // back to the list.
    const conversationId = n.metadata?.conversationId;
    navigate(conversationId ? `/sales/chat?conversationId=${conversationId}` : "/sales/chat");
  };

  const markAll = async () => {
    try {
      await markAllSalesNotificationsRead();
      setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
      setUnread(0);
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <IconButton color="primary" onClick={open} aria-label="Notifications">
        <Badge
          badgeContent={unread}
          color="error"
          max={99}
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.75rem",
              height: "18px",
              minWidth: "18px",
              borderRadius: "9px",
              fontWeight: 600,
            },
          }}
        >
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 360, borderRadius: 3, mt: 1 } } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.25 }}
        >
          <Typography sx={{ fontWeight: 700 }}>Notifications</Typography>
          {unread > 0 && (
            <Button size="small" onClick={markAll} sx={{ textTransform: "none" }}>
              Mark all read
            </Button>
          )}
        </Stack>

        {/* All notifications, or just the currently-selected customer's. The customer
            tab only appears when a customer is selected in the app. */}
        {selectedCustomer && (
          <Tabs
            value={tab}
            onChange={(_, v: "all" | "customer") => setTab(v)}
            variant="fullWidth"
            sx={{ minHeight: 40, borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <Tab
              value="all"
              label="All"
              sx={{ minHeight: 40, textTransform: "none", fontWeight: 600 }}
            />
            <Tab
              value="customer"
              label={selectedCustomerName}
              sx={{ minHeight: 40, textTransform: "none", fontWeight: 600, maxWidth: 200 }}
            />
          </Tabs>
        )}
        {!selectedCustomer && <Divider />}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : !shownItems.length ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 4, px: 2 }}
          >
            {tab === "customer"
              ? `No notifications for ${selectedCustomerName}.`
              : "You have no notifications."}
          </Typography>
        ) : (
          <List disablePadding sx={{ maxHeight: 380, overflowY: "auto" }}>
            {shownItems.map((n) => (
              <ListItemButton
                key={n.id}
                onClick={() => handleClick(n)}
                sx={{
                  gap: 1.25,
                  alignItems: "flex-start",
                  px: 2,
                  py: 1.25,
                  bgcolor: n.isRead ? "transparent" : alpha(theme.palette.primary.main, 0.06),
                }}
              >
                <Box
                  sx={{
                    mt: 0.25,
                    color: n.type === "delivery_misconduct" ? "error.main" : "primary.main",
                  }}
                >
                  {n.type === "delivery_misconduct" ? (
                    <ReportProblemOutlinedIcon fontSize="small" />
                  ) : (
                    <ChatBubbleOutlineRoundedIcon fontSize="small" />
                  )}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: n.isRead ? 500 : 700, lineHeight: 1.3 }}
                  >
                    {n.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {n.description}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {new Date(n.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}
      </Menu>
    </>
  );
};

export default SalesNotificationMenu;
